import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { BoardStep, StepDetail } from "../dtos";
import { useEscalateStep } from "../hooks/use-escalate-step.hook";
import { isReworkOnly, stepStatusText } from "../utils/derive-board";
import { resolveSla, slaClockStarted, slaTooltip } from "../utils/sla";
import EscalateButton from "./escalate-button";
import EscalateConfirmModal from "./escalate-confirm-modal";
import EscalationThreadPanel from "./escalation-thread-panel";
import KycCardPanel from "./kyc-card-panel";
import KycModal from "./kyc-modal";
import StepBody from "./step-body";
import StepStatusIcon from "./step-status-icon";
import { StatusPill } from "./status-pill";

/**
 * §MobileQA (25-Jul design, Indicator ramp): every status pill on a step row is the design's
 * `Badges` component — Body/xxs 10/16 regular, px 4 / py 0, radius `Roundness/md` (4px), on an
 * `Indicator/*-light` ground with the matching `Indicator/*-dark` ink. The tab had been drawing
 * these from the SEMANTIC accent/positive/negative ramp (#E6EFF5 on #015C9A etc.) at 11px/600,
 * which reads as a different, heavier component than every other badge in the product.
 */

/**
 * One expandable step row (mock: "iScore — Qualification for loan" with a status icon,
 * an optional chip like "Pending approval", and the dynamic form inside).
 * View-only steps (other personas' blocks / internal milestones) render as a tracker row.
 */

/**
 * §Escalate-thread: what this card can open, and what it shows once open. Three rules that only make sense
 * read together, so they are derived in one place — and module-level rather than inline, because StepCard is
 * a 500-line component already sitting on a cognitive-complexity finding, and four more boolean operators
 * inside it make a documented baseline measurably worse.
 *
 * - `showsFullComments` — the step's whole thread renders, because StepForm (which owns CommentsPanel) does.
 *   Its negation is also the status-only test: anything else is a tracker row.
 * - `showEscalationThread` — an escalation exists and the full thread is NOT rendering, which is the gap this
 *   closes. Every other surface would leave the agent with a row that says "Escalated" and nothing behind it.
 * - `expandable` — §WriteOnly-expand still holds: a locked row with nothing to read stays a status row with no
 *   chevron. An escalated one earns one, because now there is something behind it.
 */
/** Module-local: a second export from a component file costs it its Fast Refresh boundary. */
/** §41: the i-Score verdict shown on the row — the CREDIT outcome, never the step lifecycle. */
export interface IScoreOutcome {
  label: string;
  tone: "green" | "red" | "yellow";
}

/**
 * §41 + QA (prod, 2026-08-16): the i-Score pill is the CREDIT VERDICT, not the step lifecycle.
 *
 * A disqualifying score still COMPLETES the step (lifecycle APPROVED) while the flow cancels the case — the
 * old lifecycle mapping painted a 399 green "Approved". The bureau band the engine serves ("Disqualified
 * (< 520)" / "Accept w/ Clearance (520-626)" / "Qualified (> 626)") is authoritative; the lifecycle mapping
 * survives only as the fallback for pre-band pulls, which is why the band is consulted in full before status
 * is looked at at all.
 *
 * Ordered early-returns rather than the six-level ternary ladder this used to be: the nesting made the
 * precedence between band and status almost unreadable, and it was the single largest contributor to this
 * file's cognitive complexity.
 */
const resolveIScoreOutcome = (
  step: BoardStep["step"],
  isIScore: boolean
): IScoreOutcome | null => {
  if (!isIScore) return null;
  const band = step.iscoreSummary?.band ?? null;
  if (band?.startsWith("Disqualified")) return { label: "Disqualified", tone: "red" };
  if (band?.startsWith("Accept")) return { label: "Accept w/ Clearance", tone: "yellow" };
  if (band?.startsWith("Qualified")) return { label: "Qualified", tone: "green" };
  if (step.status === "APPROVED") return { label: "Approved", tone: "green" };
  if (step.status === "REJECTED") return { label: "Rejected", tone: "red" };
  return null;
};

/** §iScoreSummary: the bureau NUMBER on the title row wears the outcome's colour (design "iScore: 890"). */
const iScoreScoreColor = (outcome: IScoreOutcome | null, colors: Theme["nawy"]["colors"]): string => {
  if (outcome?.tone === "red") return colors.semantic.text.extension.negative as string;
  if (outcome?.tone === "yellow") return "#B34B00";
  return "#3E8D7B";
};

/**
 * §Reopened / §Rework-not-rejected: which icon a collapsed row wears.
 *
 * An OPEN return visit the agent can write shows the attention icon; a PURE send-back deliberately does not
 * (nothing on it was refused, so it must read as live work) and falls back to the in-progress icon. The two
 * flags interact, and that precedence is the whole point of naming this.
 */
const summaryIconStatus = (
  uiStatus: BoardStep["uiStatus"],
  reopenedLive: boolean,
  reworkOnly: boolean
): BoardStep["uiStatus"] => {
  if (reopenedLive && !reworkOnly) return "EDITS_NEEDED";
  if (reworkOnly) return "IN_PROGRESS";
  return uiStatus;
};

/**
 * The card's ground. Every branch is a QA/design bite, and each is a reason a step must NOT look untouched:
 *  - §EditsNeeded (design 25-Jul) — a sent-back card sits on pale red;
 *  - §Reopened — an OPEN return visit the AGENT can write reads the same way;
 *  - §MobileQA — BLOCKER (engine REJECTED) used to fall through to neutral, so a rejected step looked
 *    identical to an untouched one; every [Mobile] rejected frame puts it on pale red;
 *  - §Conditional — approved-with-conditions gets the warning ramp, never red;
 *  - §41 — only the NEGATIVE i-Score verdict tints. The green ground for an APPROVED pull is deliberately
 *    gone: "approved and rejected must never look identical" is carried by the check glyph and badge instead;
 *  - §Rework-not-rejected — a PURE send-back is excluded (nothing was refused); a step holding a REJECTED
 *    file or field review still earns it.
 */
const cardGround = (
  uiStatus: BoardStep["uiStatus"],
  flags: { reopenedLive: boolean; reworkOnly: boolean; iScoreOutcome: IScoreOutcome | null },
  colors: Theme["nawy"]["colors"]
): string => {
  const negative = colors.semantic.layer.extension.negativeLight as string;
  if (!flags.reworkOnly && (uiStatus === "EDITS_NEEDED" || flags.reopenedLive)) return negative;
  if (uiStatus === "BLOCKER") return negative;
  if (uiStatus === "CONDITIONAL") return "#FFF6F0";
  if (flags.iScoreOutcome?.tone === "red") return negative;
  return colors.primitives.white[200] as string;
};

const stepDisclosure = ({
  agentWritable,
  showForm,
  escalated,
  expanded,
}: {
  agentWritable: boolean;
  showForm: boolean;
  escalated: boolean;
  expanded: boolean;
}): {
  showsFullComments: boolean;
  expandable: boolean;
  renderEscalationThread: boolean;
} => {
  const showsFullComments = agentWritable && showForm;
  const showEscalationThread = escalated && !showsFullComments;
  return {
    showsFullComments,
    expandable: agentWritable || showEscalationThread,
    // Mounted only while open: AccordionDetails keeps its children mounted when collapsed, so without this
    // every step on the tab would fetch its thread on first render.
    renderEscalationThread: showEscalationThread && expanded,
  };
};

/**
 * §StepChips: the right-aligned chip rail on a collapsed step row — the i-Score verdict, reopened,
 * conditional, edits-needed, in-progress and escalated chips, the SLA label, and the Escalate control.
 *
 * A component rather than a run of conditionals inline in StepCard, because these are nine independent
 * render decisions that share nothing but a container. Inline they were the second-largest contributor to
 * the card's cognitive complexity, and they buried the card's actual shape — ground, summary, details —
 * under a wall of chip rules.
 *
 * Returns a FRAGMENT, spliced into the summary's Stack exactly where the block used to sit, so DOM order and
 * MUI Stack spacing are unchanged. That ordering is load-bearing: see the note at the top of the rail about
 * `order` reordering having broken Stack spacing once already (live bite, 2026-08-05).
 */
const StepChips = ({
  boardStep,
  iScoreOutcome,
  reopenedLive,
  reworkOnly,
  escalated,
  slaExtended,
  stepSla,
  overdue,
  escalateBusy,
  onEscalate,
}: {
  boardStep: BoardStep;
  iScoreOutcome: IScoreOutcome | null;
  reopenedLive: boolean;
  reworkOnly: boolean;
  escalated: boolean;
  /** §Late-docs: the step's SLA was re-armed because sales added documents after approval. */
  slaExtended: boolean;
  stepSla: ReturnType<typeof resolveSla>;
  overdue: boolean;
  escalateBusy: boolean;
  onEscalate: () => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const { step } = boardStep;
  return (
    <>
          {/* Right-aligned chips — AFTER the flexed title box in real DOM order: `order` reordering
              broke MUI Stack spacing (the margin landed on the ICON whenever a chip rendered, so
              chip-carrying rows started indented — live bite, 2026-08-05). */}
          {/* §41 (QA): the i-Score verdict pill — band-driven (Disqualified / Accept w/ Clearance /
              Qualified), lifecycle Approved/Rejected only for pre-band pulls. */}
          {iScoreOutcome && (
            <StatusPill
              tone={iScoreOutcome.tone}
              radius={theme.nawy.radius.md}
              label={iScoreOutcome.label}
            />
          )}
          {/* §Reopened: a return visit — the step was completed before (rework / change-unit /
              revert). Quiet slate chip; the red/orange verdict chips still explain WHY. */}
          {/* §Reopened: red while the return visit is OPEN; quiet grey once settled again.
              Not a design state — drawn on the same Indicator ramp so it sits with the others. */}
          {step.reopened && (
            <StatusPill
              tone={reopenedLive && !reworkOnly ? "red" : "grey"}
              radius={theme.nawy.radius.md}
              label="Reopened"
            />
          )}
          {/* §Conditional: approved WITH conditions — orange, informational (never blocks).
              Indicator/orange-light on orange-dark, matching the ramp the design uses elsewhere. */}
          {Boolean(step.conditionalItems?.length) && (
            <Box
              sx={{
                alignSelf: "center",
                px: "4px",
                py: 0,
                borderRadius: theme.nawy.radius.md,
                backgroundColor: "#FFE3CC",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  lineHeight: "16px",
                  fontWeight: 400,
                  textAlign: "center",
                  color: "#B34B00",
                  whiteSpace: "nowrap",
                }}
              >
                Conditional
              </Typography>
            </Box>
          )}
          {/* §EditsNeeded (design 25-Jul): red "Edits needed" chip */}
          {boardStep.uiStatus === "EDITS_NEEDED" && !reworkOnly && (
            <StatusPill tone="red" radius={theme.nawy.radius.md} label="Edits needed" />
          )}
          {/* §40 (QA): the in-progress tag, immediately left of the chevron */}
          {step.status === "IN_PROGRESS" &&
            boardStep.uiStatus !== "BLOCKER" &&
            // §Rework-not-rejected: a pure send-back IS in progress and says so — it is only the
            // rejection-flavoured EDITS_NEEDED (a refused file or field) that suppresses this pill.
            (boardStep.uiStatus !== "EDITS_NEEDED" || reworkOnly) &&
            boardStep.uiStatus !== "CONDITIONAL" && (
              <StatusPill tone="blue" radius={theme.nawy.radius.md} label="In progress" />
            )}
          {/* §Escalate-overdue: the step has already been raised. Reads off the real `escalated` column
              rather than sniffing the "⚠️ Escalated:" comment prefix, so an escalation raised by the
              ESCALATE effect shows here too. §Escalate-report put the flag on the board payload, so it no
              longer waits for a first expand.

              Sits ABOVE the §SLA comment rather than inside it: the deadline block below is what that
              comment documents, and an escalation is not an SLA reading — a step can be escalated while
              still inside its SLA, and an overdue step may never have been raised. */}
          {escalated && <StatusPill tone="red" radius={theme.nawy.radius.md} label="Escalated" />}
          {/* §Late-docs: the review's deadline was re-armed because sales added documents after approval —
              a state beside the status, like Escalated, not another status value. */}
          {slaExtended && (
            <StatusPill
              tone="yellow"
              radius={theme.nawy.radius.md}
              label="SLA extended"
              title="Documents were added after approval — the step got its full SLA again"
            />
          )}
          {/* §SLA (product ask, 2026-08-16): every unsettled row that is actually RUNNING shows its
              deadline — sales see the whole workflow's live SLAs, not only their own steps (the queue
              already did this for assigned work). Red when the deadline has passed and the work is still
              open. The original ask said every unsettled row, unopened ones included; that was narrowed
              once it turned out an unopened row can only ever show a forecast (see §SLA-unstarted).

              Read as a REMAINING window ("Due in 3h", "Overdue 5h"), not as a calendar date: SLAs are
              authored in workflow-manager in minutes/hours as well as days, and a day-granular "Due 17
              Aug" was the same string all day for a 4-hour SLA — it could not say whether the agent had
              hours left or had already breached. Shares the board's formatter so one deadline reads the
              same everywhere; the exact moment stays on the tooltip.

              §SLA-unstarted: an unopened step shows NO deadline line at all — `resolveSla` returns an empty
              label for it, hence the `stepSla.label` gate. Its `plannedEndDate` is the creation forecast,
              not a commitment, and rendering it read as a date that had been missed. The deadline appears
              the moment the step opens, anchored to when that actually happened.

              Both gates are needed, not one: an empty label means "not open yet", while `plannedEndDate`
              being absent means "no SLA authored" — and for THAT the label is the words "No SLA", which
              belongs on the board card where the pill has a fixed slot to fill, not on this row. */}
          {step.plannedEndDate &&
            stepSla.label &&
            boardStep.uiStatus !== "DONE" &&
            boardStep.uiStatus !== "SKIPPED" && (
              <Typography
                title={slaTooltip(step.plannedEndDate)}
                sx={{
                  alignSelf: "center",
                  fontSize: 11,
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontWeight: stepSla.state === "breach" ? 600 : 400,
                  color:
                    stepSla.state === "breach"
                      ? colors.semantic.text.extension.negative
                      : colors.semantic.text.tertiary,
                }}
              >
                {stepSla.label}
              </Typography>
            )}
          {/* §Escalate-overdue: in the card HEADER, next to the SLA it belongs to — not in the step
              form's action bar, which renders only for a step the agent can still WRITE. The whole
              point is a step that is stuck, and "stuck" very often means stuck on somebody else. */}
          {overdue && (
            <EscalateButton
              onClick={onEscalate}
              busy={escalateBusy}
              dense
            />
          )}
    </>
  );
};

/**
 * §StepBodyDisclosure: what an expanded card actually shows, which is four different answers.
 *
 *  - no full comments — the agent cannot work this step, so the card is a status row and says so;
 *  - KYC — §Modals (2026-07-30): the compact progress panel, the form itself having moved into the
 *    modal's document pane (frames 42983 / 44232);
 *  - expanded — §Design (12107 loan-process): the form in a WHITE inset panel on the grey card;
 *  - collapsed — nothing.
 *
 * Extracted from a three-level ternary chain in the card's JSX. Guard clauses make the precedence explicit
 * (status row beats KYC beats form) where the nesting only implied it.
 */
const StepDetailsBody = ({
  boardStep,
  showsFullComments,
  expanded,
  isKyc,
  caseUid,
  participantUid,
  customerUid,
  onPreviewKyc,
}: {
  boardStep: BoardStep;
  showsFullComments: boolean;
  expanded: boolean;
  isKyc: boolean;
  caseUid: string | null;
  participantUid: string | null;
  customerUid: string | null;
  onPreviewKyc: () => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const { step } = boardStep;

  if (!showsFullComments) {
    return (
      <Typography variant="body2" color={colors.semantic.text.secondary}>
        {`Status: ${stepStatusText(boardStep)}`}
      </Typography>
    );
  }
  if (!expanded) return null;
  if (isKyc) {
    return <KycCardPanel stepUid={step.uid} onPreview={onPreviewKyc} />;
  }
  return (
    <Box
      sx={{
        backgroundColor: colors.primitives.white[100],
        borderRadius: theme.nawy.radius.xl,
        p: 2,
      }}
    >
      <StepBody
        step={step}
        caseUid={caseUid}
        participantUid={participantUid}
        customerUid={customerUid}
      />
    </Box>
  );
};

/**
 * §MD-bypass: lives in the card SUMMARY (not the expanded body) so it reads on a collapsed card too — an
 * agent scanning the phase strip should not have to open every step to learn the phase no longer blocks
 * the application. Deliberately independent of uiStatus / agentEditableNow: the step is unaffected by the
 * bypass and stays exactly as workable as before.
 */
const PhaseBypassCaption = ({
  bypass,
}: {
  bypass?: {at: string; by: string | null; reason: string | null} | null;
}) => {
  if (!bypass) return null;
  const reason = bypass.reason ? ` Reason: ${bypass.reason}` : "";
  return (
    <Typography sx={{fontSize: 12, lineHeight: "18px", color: "#B34B00"}}>
      {`Bypassed by MD — this step no longer blocks the application; submit it when the result arrives.${reason}`}
    </Typography>
  );
};

const StepCard = ({
  boardStep,
  expanded,
  onToggle,
  caseUid,
  participantUid,
  customerUid,
  phaseBypass,
}: {
  boardStep: BoardStep;
  expanded: boolean;
  onToggle: () => void;
  caseUid: string | null;
  participantUid: string | null;
  customerUid: string | null;
  /** §MD-bypass: the step's own PHASE bypass stamp (from the board phase this card's section
   * belongs to) — non-null while a Managing Director has bypassed it. Sourced from the board so
   * the caption below renders on a collapsed card, without waiting on the step detail fetch. */
  phaseBypass?: {at: string; by: string | null; reason: string | null} | null;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const { step } = boardStep;
  // §SLA: the remaining window on this step's deadline, in the board's vocabulary (see utils/sla).
  // §SLA-unstarted: an unopened step's `plannedEndDate` is the creation forecast, not a commitment — it
  // reads as a plan and can never read as a breach, so `started` decides which vocabulary applies.
  const stepStarted = slaClockStarted(step.status);
  const stepSla = resolveSla(step.plannedEndDate, { started: stepStarted });
  // §W3.3: KYC gets a view-only recap + Print modal (opened from the card, doesn't toggle it)
  const [kycOpen, setKycOpen] = useState(false);
  /**
   * §Escalate-overdue: breached AND still open. `stepSla.state` covers a missing/invalid deadline
   * ("none"), and the settled check keeps the button off a step that merely finished late — that is
   * not stuck, and escalating it would page a manager about nothing.
   */
  const overdue =
    stepSla.state === "breach" &&
    boardStep.uiStatus !== "DONE" &&
    boardStep.uiStatus !== "SKIPPED";
  const escalate = useEscalateStep();
  const [confirming, setConfirming] = useState(false);
  /**
   * Cache-only read of the step detail: `enabled: false` means this NEVER issues a fetch of its own —
   * the card must not turn a chip into a request per row. It reads whatever `useStepDetail` has already
   * put in the cache (from an expand, or from the invalidation after an escalation), so the chip flips
   * on the moment the detail refetches and stays absent until then.
   */
  const cachedDetail = useQuery<StepDetail>({
    queryKey: ["nawy-now", "step", step.uid],
    enabled: false,
  }).data;
  /**
   * §Escalate-report: the board payload now carries `escalated`, so the chip is right on first paint instead of
   * only after an expand. The cached detail stays as the second source — it is FRESHER than the board within a
   * session (escalating invalidates the detail, and the board row may still be the pre-escalation copy), so the
   * two are OR-ed rather than one replacing the other. Neither can wrongly say "escalated": the flag is never
   * cleared once set.
   */
  const escalated = step.escalated === true || cachedDetail?.escalated === true;
  // §Late-docs: same two sources, same OR — the board carries the flag, the cached detail carries the list.
  const slaExtended =
    step.slaExtended === true || Boolean(cachedDetail?.slaExtensions?.length);
  const isKyc = step.stepName === "KYC";
  // §41 (QA): the i-Score card colour-codes its outcome — pale green/red ground + an outcome pill —
  // so an approved and a rejected application are never visually identical. (The inline score value
  // needs the engine to serve the score fact on the persona step — recorded as a WM read-model ask.)
  const isIScore = /i-?score/i.test(step.stepName);
  // §41 + QA (prod, 2026-08-16): the pill is the CREDIT VERDICT, not the step lifecycle. A
  // disqualifying score still COMPLETES the step (lifecycle APPROVED) while the flow cancels the
  // case — the old lifecycle mapping painted a 399 green "Approved". The bureau band the engine
  // serves ("Disqualified (< 520)" / "Accept w/ Clearance (520–626)" / "Qualified (> 626)") is
  // authoritative; the lifecycle mapping stays only as the fallback for pre-band pulls.
  const iScoreOutcome = resolveIScoreOutcome(step, isIScore);

  // §AgentAccess: in the CRM (agent channel) form-vs-tracker follows the AGENT's access, not the
  // customer's. An agent fills a co-applicant's block on their behalf, so a step that is customer
  // 'view' (the primary the board is scoped to only watches the joint/guarantor block) still renders
  // its form for the agent. 'none' is a pure status row; the customer app (no agentAccess) keeps the
  // customer `access` gate. StepBody fetches the full internal schema, so an empty persona schema is fine.
  const agentLevel = step.agentAccess?.level;
  const showForm = agentLevel
    ? agentLevel !== "none"
    : step.access !== "view";

  // §Reopened (product ask, 2026-08-06): an OPEN reopened step is red like a sent-back one —
  // same ground, same attention icon — the chip still says "Reopened" so the WHY stays honest.
  // Red ONLY when the AGENT holds write on it (follow-up ask): a reviewer's reopened step is
  // not the agent's debt, so it keeps the quiet slate chip.
  const agentWritable =
    step.clientAccess?.AGENT !== "view" &&
    (!step.agentAccess || step.agentAccess.level === "write");
  const reopenedLive =
    Boolean(step.reopened) && step.status === "IN_PROGRESS" && agentWritable;
  /**
   * §Rework-not-rejected: the flow RETURNED this step to the agent; nothing on it was refused. Red is
   * reserved for a refusal, so a send-back reads like any other live step of theirs — neutral ground,
   * the in-progress ring, and a quiet "Reopened" chip carrying the why. The step keeps its
   * EDITS_NEEDED uiStatus, so the next-action banner and the urgent list still point here.
   */
  const reworkOnly = isReworkOnly(step);

  // §WriteAffordance narrowed (product ask, 2026-08-13): the blue cue marks what needs the agent
  // NOW — an OPEN step they can edit — not everything they hold write capability on, so a settled
  // step's recap row goes quiet again. WM's `agentAccess.editable` is exactly that fold (write
  // capability × IN_PROGRESS/REWORK_REQUIRED × not precondition-locked); an older WM deploy ships
  // no agentAccess, so those fall back to the open statuses. Expansion stays on `agentWritable` —
  // a settled step the agent owns still opens for its recap.
  const agentEditableNow =
    agentWritable &&
    (step.agentAccess
      ? step.agentAccess.editable
      : step.status === "IN_PROGRESS" || step.status === "REWORK_REQUIRED");

  // §Escalate-thread: the full comment thread lives inside StepForm, so it renders only where the agent can
  // both open the card AND get a form. Anywhere else an escalation is invisible — which is exactly a step
  // stuck on somebody else, the thing escalation is FOR. See `stepDisclosure` for the three rules.
  const {showsFullComments, expandable, renderEscalationThread} = stepDisclosure({
    agentWritable,
    showForm,
    escalated,
    expanded,
  });

  return (
    <>
    <Accordion
      id={`nawy-now-step-${step.uid}`}
      // §WriteOnly-expand (product ask, 2026-08-06): a step the agent can't WRITE is a status
      // row, full stop — name + chips, no chevron, no expansion. The write capability axis
      // (not the state fold): a settled step the agent owns still opens for its recap.
      expanded={expandable && expanded}
      onChange={expandable ? onToggle : undefined}
      disableGutters
      elevation={0}
      sx={{
        // design collapsed step-card ground is white[200] (#F4F7F8), not layer.secondary;
        // §41: the settled i-Score card tints to its outcome (design d_iscore Approved/Rejected)
        backgroundColor: cardGround(boardStep.uiStatus, {reopenedLive, reworkOnly, iScoreOutcome}, colors),
        // §WriteAffordance (product ask, 2026-08-10): a grey outline marks the rows the agent can
        // actually work, so "what needs me" is readable without opening anything. Drawn in
        // Border/secondary — the SAME grey as the container these cards sit in and the selected
        // folder tab above them, so the three borders on this screen read as one system rather than
        // three greys. (Border/primary #C9D4DB was louder but matched nothing else here.)
        // Kept TRANSPARENT on the rest so writable and read-only rows stay exactly the same height
        // and the list never jitters as steps settle.
        // The outline is the agent's "this one is yours" cue, so it wears the same accent the
        // next-action banner does (border.accent = #015C9A) rather than a neutral hairline —
        // one blue vocabulary for "you can act here". Only a step that is editable NOW earns it;
        // settled or not-yet-open rows stay transparent, which also keeps every row exactly the
        // same height so the list never jitters as steps settle.
        border: `1px solid ${
          agentEditableNow ? colors.semantic.border.accent : "transparent"
        }`,
        // !important: MUI's first/last-of-type rounding beats plain sx
        borderRadius: `${theme.nawy.radius.xl} !important`,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          expandable ? (
            <ChevronDown size={16} color={colors.semantic.text.secondary} />
          ) : undefined
        }
        sx={{
          // design: the Expandable widget is p-16 on EVERY breakpoint — a 311x50 row inside a 375
          // frame. The mobile shave to 8px was added when the title was 14/20 and the chips 11/600;
          // at the design's 12/18 title and 10/16 badges the inner width at 390px is ~330 against
          // the design's 311, so the padding is affordable and the row height matches again.
          //
          // §CardDensity: reverted to the design's 16 on both breakpoints. The 12px shave was aimed
          // at "too much white space", but the desktop frame (12107:48053) shows a 50px collapsed
          // card — 16 + 18 + 16 — and only 8px BETWEEN cards. The gap was carrying the excess, not
          // the padding, so the room now sits inside the card where the design puts it.
          p: 2,
          // §WriteOnly-expand: a locked row doesn't invite a click it won't honor
          ...(expandable ? {} : { cursor: "default", "&:hover": { cursor: "default" } }),
          minHeight: "unset",
          "&.Mui-expanded": { minHeight: "unset" },
          "& .MuiAccordionSummary-content": {
            m: 0,
            "&.Mui-expanded": { m: 0 },
          },
        }}
      >
        <Stack
          direction="row"
          // design ([Mobile] frames, node I12107:61192;5841:109548 "Title"): the row is
          // `gap-[var(--xs,8px)]` — glyph, title and badges all sit 8px apart. 12px was the
          // desktop-era spacing carried over; at the 12/18 title it left the chips adrift.
          spacing={1}
          useFlexGap
          alignItems="flex-start"
          // §Mobile: title + up to three chips don't fit one row at 390px — the crushed title
          // used to push the status icon outside the card. Let the chips wrap under the title.
          sx={{ flex: 1, pr: 1, flexWrap: "wrap", rowGap: 0.5, minWidth: 0 }}
        >
          <StepStatusIcon
            status={summaryIconStatus(boardStep.uiStatus, reopenedLive, reworkOnly)}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              // §Mobile: keep the title readable before chips are allowed onto this row
              flexBasis: { mobile: "45%", tablet: 0 },
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  // design (25-Jul, [Mobile] frames measured): the Expandable-widget title row is
                  // 279x18 inside a 311x50 card — Body/sm - Regular, 12/18/400, Text/Primary.
                  fontSize: 12,
                  lineHeight: "18px",
                  // An editable-now row's title matches the blue status bar's title — 600 on
                  // text.accent — so the outline and the label read as one cue. Settled and
                  // read-only rows keep the quiet 400/Text-Primary treatment.
                  fontWeight: agentEditableNow ? 600 : 400,
                  color: agentEditableNow
                    ? colors.semantic.text.accent
                    : colors.semantic.text.primary,
                }}
              >
                {/* §iScoreSummary: the bureau outcome belongs ON the row (design "iScore: 890" —
                    the NUMBER wears the outcome colour, green when the pull qualified) */}
                {step.iscoreSummary?.score != null ? (
                  <>
                    {step.stepName}:{" "}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: iScoreScoreColor(iScoreOutcome, colors),
                      }}
                    >
                      {step.iscoreSummary.score}
                    </Box>
                  </>
                ) : (
                  step.stepName
                )}
              </Typography>
              {/* §MobileQA: every [Mobile] rejected frame draws this as a RED BADGE on the row
                  (Indicator/red-light on red-dark), not the desktop-era bare red caption. */}
              {boardStep.uiStatus === "BLOCKER" && (
                <StatusPill
                  tone="red"
                  radius={theme.nawy.radius.md}
                  label={stepStatusText(boardStep)}
                />
              )}
              {/* §W3.4: form complete, awaiting submit — "Ready" pill (design KYC-Ready is on the
                  YELLOW indicator ramp, not the orange warning ramp) */}
              {boardStep.uiStatus === "READY" && (
                <StatusPill tone="yellow" radius={theme.nawy.radius.md} label="Ready" />
              )}
              {/* §MobileQA: the design puts a status badge on EVERY row — the Approved frames show
                  a green badge beside each settled step. Only the check glyph rendered here, so a
                  settled step and an untouched one differed by an icon alone. The engine already
                  ships the wording (step.statusBadge → boardStep.badge); i-Score keeps its own
                  outcome pill below, so it is excluded to avoid two badges saying the same thing. */}
              {boardStep.uiStatus === "DONE" && !iScoreOutcome && (
                <StatusPill
                  tone="green"
                  radius={theme.nawy.radius.md}
                  label={stepStatusText(boardStep)}
                />
              )}
              {/* §AgentVisible: the "Internal" chip is deliberately NOT rendered (product ask,
                  2026-08-10). `internalOnly` says the CLIENT never sees this step in their app —
                  which is information for the client-facing surface, not for the agent, who only
                  ever works inside the CRM. On a row that already carries a status pill it read as
                  a second, competing badge saying nothing actionable. The flag still ships on the
                  step; nothing else about it changed. */}
            </Stack>
            {/* design (25-Jul, live bite 2026-08-05): the collapsed row carries NO explainer text —
                the step intro was noise on every card. Only the dependency note survives, because
                "why can't I open this?" is state, not description. */}
            {boardStep.subtitle?.startsWith("Blocked by") && (
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: "18px",
                  color: colors.semantic.text.secondary,
                }}
              >
                {boardStep.subtitle}
              </Typography>
            )}
            <PhaseBypassCaption bypass={phaseBypass} />
          </Box>
          <StepChips
            boardStep={boardStep}
            iScoreOutcome={iScoreOutcome}
            reopenedLive={reopenedLive}
            reworkOnly={reworkOnly}
            escalated={escalated}
            slaExtended={slaExtended}
            stepSla={stepSla}
            overdue={overdue}
            escalateBusy={escalate.isLoading}
            onEscalate={() => setConfirming(true)}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { mobile: 1, tablet: 2 }, pb: 2, pt: 0 }}>
        {/* status-only when the agent can't work it (agentAccess 'none', or customer 'view' in the app).
            §Escalate-thread: this is `!showsFullComments`, which now also covers a card opened purely to
            READ an escalation — without that, a read-level step (`showForm` true, `agentWritable` false)
            would answer the new chevron with the whole form, a much larger grant than "see the escalation". */}
        <StepDetailsBody
          boardStep={boardStep}
          showsFullComments={showsFullComments}
          expanded={expanded}
          isKyc={isKyc}
          caseUid={caseUid}
          participantUid={participantUid}
          customerUid={customerUid}
          onPreviewKyc={() => setKycOpen(true)}
        />
        {/* §Escalate-thread: below whatever the row could already show, because the escalation is the
            reason this card opens at all in the no-access case — and where the form DOES render, its own
            CommentsPanel already carries the escalation, so this stays out of the way rather than
            printing the same conversation twice. Mounted only while expanded: the fetch is per step, and
            a lead's tab can hold forty of them. */}
        {renderEscalationThread && caseUid && (
          <EscalationThreadPanel stepUid={step.uid} caseUid={caseUid} />
        )}
      </AccordionDetails>
    </Accordion>
    {isKyc && (
      <KycModal stepUid={step.uid} open={kycOpen} onClose={() => setKycOpen(false)} agentViewOnly={step.clientAccess?.AGENT === "view"} />
    )}
    {/* §Escalate-overdue: hosted beside the Accordion, like KycModal — inside it, the modal would
        unmount with a collapse and take the half-typed note with it. */}
    <EscalateConfirmModal
      stepName={confirming ? step.stepName : null}
      stepUid={confirming ? step.uid : null}
      caseUid={caseUid}
      busy={escalate.isLoading}
      onCancel={() => setConfirming(false)}
      onConfirm={(note, attachmentUids) =>
        escalate.mutateAsync({ stepUid: step.uid, content: note, attachmentUids })
      }
    />
    </>
  );
};

export default StepCard;
